import moment from 'moment';
import { put, takeLatest, select, call, delay } from 'redux-saga/effects';
import { is } from 'ramda';
import { toastr } from 'react-redux-toastr';

import {
  GET_ALL_ACTIVITIES_BY_ASSIGNEE,
  GET_TASK_INFORMATION,
  GET_DUPLICATE_PROSPECTS,
  REMOVE_PROSPECT_DUPLICATE,
  MERGE_PROSPECT_DUPLICATE,
} from './constants';
import {
  getAllActivitiesByAssigneeSuccess,
  getAllActivitiesByAssigneeError,
  getTaskInformationSuccess,
  getTaskInformationError,
  clearActivitiesByAssignee,
  getDuplicateProspectsSuccess,
  getDuplicateProspectsError,
  removeProspectDuplicateSuccess,
  removeProspectDuplicateError,
  getDuplicateProspects,
  mergeProspectDuplicateSuccess,
  mergeProspectDuplicateError,
} from './actions';
import ActivityService from '../../services/activityService';
import TaskService from '../../services/taskService';
import {
  selectCurrentUserOrganizationId,
  selectSelectedProperty,
} from '../App/selectors';
import type { Saga } from 'redux-saga';

export function* fetchgetAllActivitiesByAssignee(action: Object): Saga<void> {
  try {
    yield put(clearActivitiesByAssignee());
    const organizationId = yield select(selectCurrentUserOrganizationId);
    const selectedProperty = yield select(selectSelectedProperty);
    const assigneeId = action.payload.assigneeId;
    const activityService = new ActivityService();

    let start =
      assigneeId === 'all'
        ? moment().startOf('day')
        : moment().startOf('month').subtract(7, 'days');
    let end = moment().endOf('month').add(7, 'days');

    if (action?.payload?.dates) {
      const dates = action.payload.dates;
      if (is(Array, dates)) {
        start = dates[0];
        end = dates[dates.length - 1];
      } else {
        start = dates.start;
        end = dates.end;
      }
    }

    const activities = yield activityService.getByAssignee(
      assigneeId,
      selectedProperty.id,
      organizationId,
      {
        start,
        end,
      },
    );

    yield put(getAllActivitiesByAssigneeSuccess(activities));
  } catch (err) {
    yield put(getAllActivitiesByAssigneeError(err));
  }
}

export function* getAllActivitiesByAssigneeSaga(): Saga<void> {
  yield takeLatest(
    GET_ALL_ACTIVITIES_BY_ASSIGNEE,
    fetchgetAllActivitiesByAssignee,
  );
}

export function* fetchGetTaskInformation({ payload }: Object): Saga<void> {
  try {
    payload?.isLoadingToggle?.(true);
    const organizationId = yield select(selectCurrentUserOrganizationId);
    const selectedProperty = yield select(selectSelectedProperty);
    const taskService = new TaskService();
    const tasks = yield call(
      taskService.getAll,
      selectedProperty.id,
      organizationId,
    );
    yield put(getTaskInformationSuccess(tasks));
    payload?.isLoadingToggle?.(false);
  } catch (err) {
    yield put(getTaskInformationError(err));
  }
}

export function* getTaskInformationSaga(): Saga<void> {
  yield takeLatest(GET_TASK_INFORMATION, fetchGetTaskInformation);
}

export function* fetchGetDuplicateProspects(): Saga<void> {
  try {
    const organizationId = yield select(selectCurrentUserOrganizationId);
    const selectedProperty = yield select(selectSelectedProperty);
    const taskService = new TaskService();
    const duplicateProspects = yield call(
      taskService.getDuplicateProspects,
      organizationId,
      selectedProperty.id,
    );

    yield put(getDuplicateProspectsSuccess(duplicateProspects));
  } catch (err) {
    yield put(getDuplicateProspectsError(err));
  }
}

export function* getDuplicateProspectsSaga(): Saga<void> {
  yield takeLatest(GET_DUPLICATE_PROSPECTS, fetchGetDuplicateProspects);
}

export function* fetchRemoveProspectDuplicate(action: Object): Saga<void> {
  try {
    const { id } = action.payload;
    const organizationId = yield select(selectCurrentUserOrganizationId);
    const selectedProperty = yield select(selectSelectedProperty);
    const taskService = new TaskService();
    yield call(
      taskService.removeProspectDuplicate,
      organizationId,
      selectedProperty.id,
      id,
    );

    yield put(removeProspectDuplicateSuccess());
    toastr.success(
      'Success',
      'This guest card has been marked as "Not a Match" and removed from the queue.',
    );
  } catch (err) {
    // Check if duplicate was already processed by another user
    if (
      err?.statusCode === 409 ||
      err?.code === 'DUPLICATE_ALREADY_PROCESSED'
    ) {
      yield put(removeProspectDuplicateSuccess());
      toastr.info(
        'Info',
        'This duplicate was already processed by another user and has been removed from the queue.',
      );
    } else {
      yield put(removeProspectDuplicateError(err));
      toastr.error('Error', 'Failed to mark as not a match');
    }
  } finally {
    yield delay(1000);
    yield put(getDuplicateProspects());
  }
}

export function* removeProspectDuplicateSaga(): Saga<void> {
  yield takeLatest(REMOVE_PROSPECT_DUPLICATE, fetchRemoveProspectDuplicate);
}

export function* fetchMergeProspectDuplicate(action: Object): Saga<void> {
  try {
    const { id, mergeData } = action.payload;
    const organizationId = yield select(selectCurrentUserOrganizationId);
    const selectedProperty = yield select(selectSelectedProperty);
    const taskService = new TaskService();

    yield call(
      taskService.mergeProspectDuplicate,
      organizationId,
      selectedProperty.id,
      id,
      mergeData,
    );

    yield put(mergeProspectDuplicateSuccess());
    toastr.success(
      'Success',
      'The duplicate contact has been successfully merged.',
    );
  } catch (err) {
    // Check if duplicate was already processed by another user (in error response)
    if (
      err?.statusCode === 409 ||
      err?.code === 'DUPLICATE_ALREADY_PROCESSED'
    ) {
      yield put(mergeProspectDuplicateSuccess());
      toastr.info(
        'Info',
        'This duplicate was already processed by another user and has been removed from the queue.',
      );
    } else {
      yield put(mergeProspectDuplicateError(err));
      toastr.error('Error', 'Failed to merge the duplicate contact');
    }
  } finally {
    yield delay(1000);
    yield put(getDuplicateProspects());
  }
}

export function* mergeProspectDuplicateSaga(): Saga<void> {
  yield takeLatest(MERGE_PROSPECT_DUPLICATE, fetchMergeProspectDuplicate);
}

export default [
  getAllActivitiesByAssigneeSaga,
  getTaskInformationSaga,
  getDuplicateProspectsSaga,
  removeProspectDuplicateSaga,
  mergeProspectDuplicateSaga,
];
